import { Component, OnInit, ViewChild, OnDestroy } from '@angular/core';
import { NgForm } from '@angular/forms';
import { Vendedor } from '../../core/models/vendedor';
import { Usuario } from '../../core/models/usuario';
import { VendedorService } from 'src/app/core/services/vendedor.service';
import { UsuarioService } from 'src/app/core/services/usuario.service';
import { MessageService, ConfirmationService } from 'primeng/api';
import { Subject } from 'rxjs';

@Component({
  selector: 'app-vendedor',
  templateUrl: './vendedor.component.html',
  styleUrls: ['./vendedor.component.scss'],
})
export class VendedorComponent implements OnInit, OnDestroy {
  vendedor = new Vendedor();
  vendedores: Vendedor[] = [];

  // edição
  editMode = false;
  editVendedorId: number | null = null;

  @ViewChild('clienteForm') vendedorForm?: NgForm;

  usuarios: Usuario[] = [];
  selectedUsuarioId: number | null = null;

  private destroy$ = new Subject<void>();

  constructor(
    private readonly servico: VendedorService,
    private readonly usuarioService: UsuarioService,
    private readonly messageService: MessageService,
    private readonly confirmation: ConfirmationService
  ) {}

  selecionar(): void {
    this.servico.selecionar().subscribe((retorno) => (this.vendedores = retorno));
  }

  // Seleciona vendedores e tenta preencher `usuario` a partir da lista de usuarios carregada
  selecionarComUsuarios(): void {
    this.servico.selecionar().subscribe({
      next: (retorno) => {
        // Se já temos lista de usuarios, mapear
        this.vendedores = (retorno || []).map((v: any) => {
          // se usuario não vier embutido, mas houver usuarioId, preenche a partir do cache de usuarios
          if ((!v.usuario || !v.usuario.username) && v.usuarioId && this.usuarios && this.usuarios.length) {
            const found = this.usuarios.find((u) => String(u.id) === String(v.usuarioId));
            if (found) v.usuario = found;
          }
          return v;
        });
      },
      error: (err) => {
        console.error('Erro ao carregar vendedores:', err);
        this.vendedores = [];
      },
    });
  }

  salvarAlteracao(): void {
    if (!this.editMode || this.editVendedorId == null) return;
    // sincroniza usuário selecionado antes de enviar alteração
    if (this.selectedUsuarioId) {
      this.vendedor.usuario = { id: this.selectedUsuarioId } as unknown as Usuario;
    } else {
      this.vendedor.usuario = null;
    }
    this.servico.alterar(this.editVendedorId, this.vendedor).subscribe({
      next: (ret) => {
        const idx = this.vendedores.findIndex((x) => x.id === this.editVendedorId);
        if (idx >= 0) this.vendedores[idx] = ret;
        this.messageService.add({ severity: 'success', summary: 'Sucesso', detail: 'Vendedor alterado com sucesso.' });
        this.cancelarEdicao();
      },
      error: (err) => {
        this.messageService.add({ severity: 'error', summary: 'Erro', detail: 'Erro ao alterar vendedor.' });
        console.error('Erro ao alterar vendedor:', err);
      },
    });
  }

  confirmarAtivarDesativar(vendedor: Vendedor | undefined): void {
    if (!vendedor?.id) return;
    const willDeactivate = !!vendedor.ativo;
    const actionLabel = willDeactivate ? 'desativar' : 'ativar';

    this.confirmation.confirm({
      message: `Confirma ${actionLabel} o vendedor ${vendedor.nome || vendedor.id}?`,
      acceptLabel: 'Sim',
      rejectLabel: 'Não',
      accept: () => {
        this.servico.ativarDesativar(vendedor.id as number, !willDeactivate).subscribe({
          next: (ret) => {
            const idx = this.vendedores.findIndex((u) => u.id === vendedor.id);
            if (idx >= 0) {
              if (ret) this.vendedores[idx] = ret;
              else this.vendedores[idx].ativo = !willDeactivate;
            }
            this.messageService.add({ severity: 'success', summary: 'Sucesso', detail: `Vendedor ${willDeactivate ? 'desativado' : 'ativado'} com sucesso.` });
          },
          error: (err) => {
            const msg = err?.error?.message || err?.message || `Erro ao ${actionLabel} vendedor`;
            this.messageService.add({ severity: 'error', summary: 'Erro', detail: msg });
            console.error(`Erro ao ${actionLabel} vendedor:`, err);
          },
        });
      },
    });
  }

  editar(c: Vendedor): void {
    this.editMode = true;
    this.editVendedorId = c.id ?? null;
    this.vendedor = { ...c } as Vendedor;
    // preenche o selectedUsuarioId para que o dropdown mostre o usuário associado
    const uid = c.usuario?.id ?? (c as any).usuarioId ?? null;
    this.selectedUsuarioId = uid !== null && uid !== undefined ? Number(uid) : null;
  }

  cancelarEdicao(): void {
    this.editMode = false;
    this.editVendedorId = null;
    this.vendedor = new Vendedor();
    this.selectedUsuarioId = null;
    try {
      this.vendedorForm?.resetForm(this.vendedor);
    } catch (e) {
      // ignore if form not available
    }
  }

  //Método de inicialização
  ngOnInit() {
    // Carregar lista de usuarios primeiro, depois carregar vendedores e mapear nomes quando possível
    this.usuarioService.selecionar().subscribe({
      next: (retorno) => {
        console.log('Usuarios recebidos:', retorno);
        // Normaliza nomes de propriedade para optionLabel
        this.usuarios = (retorno || []).map((u: any) => {
          if (!u) return u;
          // garantir que id seja number (coerção) para casar com optionValue
          if (u.id !== undefined && u.id !== null) {
            u.id = Number(u.id);
          }
          if (!u.username && (u.nome || u.login)) {
            u.username = u.nome || u.login;
          }
          return u;
        });
        // agora carregamos vendedores e preenchemos usuario quando possível
        this.selecionarComUsuarios();
      },
      error: (err) => {
        console.error('Erro ao carregar usuarios:', err);
        // manter a aplicação funcional mesmo se falhar - ainda tentamos carregar vendedores
        this.usuarios = [];
        this.selecionar();
      },
    });
  }

  cadastrar(): void {
    if (this.selectedUsuarioId) {
      // atribui objeto mínimo com id
      this.vendedor.usuario = { id: this.selectedUsuarioId } as unknown as Usuario;
    } else {
      this.vendedor.usuario = null;
    }

    this.servico.cadastrar(this.vendedor).subscribe({
      next: (retorno) => {
        this.vendedores.push(retorno);
        this.vendedor = new Vendedor();
        this.selectedUsuarioId = null;
      },
      error: (err) => {
        // O ErrorInterceptor já formata o erro para conter { status, statusText, message, body }
        // Mas o backend pode retornar message como string ou dentro de body
        let userMessage = 'Erro ao cadastrar cliente';

        if (!err) {
          userMessage = 'Erro desconhecido';
        } else if (typeof err === 'string') {
          userMessage = err;
        } else if (err.message) {
          userMessage = err.message;
        } else if (err.body) {
          if (typeof err.body === 'string') userMessage = err.body;
          else if (err.body.message) userMessage = err.body.message;
          else userMessage = JSON.stringify(err.body);
        }

        this.messageService.add({ severity: 'error', summary: 'Erro', detail: userMessage });
        // Mantém log técnico para debug
        console.error('Erro ao cadastrar cliente:', err);
      }      
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
